import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { type AtticErrorFragment, useAtticApiKeyCreateMutation } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { useState } from "react";
import { useIntl } from "react-intl";

import AtticApiKeyCreatePage from "../components/AtticApiKeyCreatePage/AtticApiKeyCreatePage";
import AtticApiKeySecretDialog from "../components/AtticApiKeySecretDialog/AtticApiKeySecretDialog";
import { getExpiryDayError } from "../errors";
import { sectionMessages } from "../messages";
import { type AtticApiKeyFormData } from "../types";
import { atticApiKeyUrl } from "../urls";
import { expiryDayToIso, todayLocalDay } from "../utils";
import { usePermissionChoices } from "./usePermissionChoices";

export const AtticApiKeyCreateView = () => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const { choices } = usePermissionChoices();
  const [errors, setErrors] = useState<AtticErrorFragment[]>([]);
  // The new key's secret, shown once, then gone. It is held here and not
  // passed on to the detail page, so it never lands in the URL or history.
  const [created, setCreated] = useState<{ id: string; secret: string } | null>(null);
  // The hook keeps no result (ignoreResults), so the save state is kept here.
  const [saveState, setSaveState] = useState<ConfirmButtonTransitionState>("default");
  const [createKey] = useAtticApiKeyCreateMutation({
    // The payload carries the one-time secret, so it is kept out of the Apollo
    // cache (no-cache) and out of the hook's own result (ignoreResults: this
    // Apollo, 3.4, has no reset()); onCompleted still receives it. The detail
    // page this opens reads the new key from the network.
    fetchPolicy: "no-cache",
    ignoreResults: true,
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const payload = result.atticApiKeyCreate;
      const refused = payload?.errors ?? [];

      setErrors(refused);

      if (payload && !refused.length && payload.apiKey && payload.secret) {
        setSaveState("success");
        notify({ status: "success", text: intl.formatMessage(sectionMessages.created) });
        setCreated({ id: payload.apiKey.id, secret: payload.secret });
      } else {
        setSaveState("error");
      }
    },
    onError: () => setSaveState("error"),
  });

  const handleSubmit = (data: AtticApiKeyFormData, expiryUnreadable: boolean) => {
    // A day this cannot read is refused, not dropped: dropping it would issue
    // a key that never expires.
    const expiryError = getExpiryDayError(
      data.expiryDay,
      todayLocalDay(Date.now()),
      intl,
      expiryUnreadable,
    );

    if (expiryError) {
      setErrors([expiryError]);
      setSaveState("error");

      return;
    }

    const expiresAt = data.expiryDay ? expiryDayToIso(data.expiryDay) : null;

    setErrors([]);
    setSaveState("loading");
    createKey({
      variables: {
        input: {
          label: data.label.trim(),
          permissions: data.permissions,
          readOnly: data.readOnly,
          ...(expiresAt ? { expiresAt } : {}),
        },
      },
    });
  };

  return (
    <>
      <AtticApiKeyCreatePage
        permissionChoices={choices}
        errors={errors}
        disabled={saveState === "loading" || created !== null}
        saveButtonBarState={saveState}
        minDay={todayLocalDay(Date.now())}
        onSubmit={handleSubmit}
      />
      <AtticApiKeySecretDialog
        kind="created"
        secret={created?.secret ?? null}
        onClose={() => {
          if (created) {
            const { id } = created;

            setCreated(null);
            navigate(atticApiKeyUrl(id), { replace: true });
          }
        }}
      />
    </>
  );
};

export default AtticApiKeyCreateView;
